import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, type RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { SynergyFacilityOptionStartAdornment } from "../../../pages/Profile/v2/SynergyCredentials/SynergyFacilityOptionStartAdornment";
import { useAddSingleShiftByDateAndNurse } from "../../../pages/Profile/v2/WeeklySchedule/XWeekScheduleGrid/hooks";
import { INK, STATUS } from "../../../theme/tokens";
import type { DepartmentLocation } from "../../../utils/models";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { DateInput } from "../../UIToolkit/Inputs/DateInput";
import { StaffAutoComplete } from "../../UIToolkit/Inputs/StaffAutoComplete";
import { SystemAutoComplete } from "../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { type SynergySingleShiftAdder, useSynergyMinimalShiftAdditionState } from "./useSynergyMinimalShiftAdditionState";

const minWidth = "350px";

type Props = {
	mode: "add-recurring" | "add-single";
	facility?: DepartmentLocation;
	clickedDate: DateTime;
	onClose?: ((didUpdateOrCreate?: boolean) => void) | ((didUpdateOrCreate?: boolean) => Promise<void>);
	/**
	 * Ref from parent to control did change in inner state.
	 */
	didChangeRef?: RefObject<boolean>;
	/**
	 * Triggered when the didChangeRef is true, and we attempt to close
	 */
	onWarnOnClose?: () => void;
};

type FormProps = Omit<Props, "clickedDate"> & {
	currentDate: DateTime;
	setCurrentDate: (date: DateTime) => void;
	singleShift: SynergySingleShiftAdder | undefined;
};

/**
 * Only the add-single form mounts the one-off shift hook: it writes into Staffing's data on the provider, and the
 * add-recurring form (Master schedule) has no reason to subscribe to it.
 */
export const SynergyMinimalShiftAddition: FC<Props> = ({ clickedDate, ...props }) => {
	const [currentDate, setCurrentDate] = useState<DateTime>(clickedDate);
	return props.mode === "add-single" ? (
		<SynergyMinimalSingleShiftAddition {...props} currentDate={currentDate} setCurrentDate={setCurrentDate} />
	) : (
		<SynergyMinimalShiftAdditionForm {...props} currentDate={currentDate} setCurrentDate={setCurrentDate} singleShift={undefined} />
	);
};

const SynergyMinimalSingleShiftAddition: FC<Omit<FormProps, "singleShift">> = (props) => {
	const singleShift = useAddSingleShiftByDateAndNurse({ selectedDate: props.currentDate, onCloseCalled: () => props.onClose?.(true) });
	return <SynergyMinimalShiftAdditionForm {...props} singleShift={singleShift} />;
};

const SynergyMinimalShiftAdditionForm: FC<FormProps> = ({ mode, facility, currentDate, setCurrentDate, singleShift, onClose, didChangeRef, onWarnOnClose }) => {
	const { t } = useTranslation();
	const {
		state,
		handleSetState,
		nurseListToShow,
		credentialedNurseIds,
		handleNurseChange,
		facilitiesList,
		onSelectedFacilityChange,
		saveDisabled,
		handleSaveClick,
		handleCloseClick,
		isCreateOrUpdateRecurringShiftsLoading,
		toggleShowCredentialedOnly,
		isLoadingCredentials,
		isRosterLoading,
		isRosterError,
	} = useSynergyMinimalShiftAdditionState({ mode, facility, currentDate, singleShift, onClose, didChangeRef, onWarnOnClose });

	const { track } = useAnalytics();

	useEffect(() => {
		track("synergy_minimal_shift_addition_opened", {
			mode,
		});
	}, [track, mode]);

	return (
		<Box
			sx={{
				height: "fit-content",
				display: "flex",
				flexDirection: "column",
				gap: 2,
				padding: 2,
			}}
		>
			<Box
				sx={{
					flex: 1,
					display: "flex",
					gap: 1,
					alignItems: "center",
				}}
			>
				<Box
					sx={{
						display: "flex",
						gap: 0.5,
						alignItems: "end",
					}}
				>
					<DateInput value={currentDate} onChange={(newValue) => setCurrentDate(newValue)} minDate={DateTime.now().startOf("week")} />
					<Typography
						sx={{
							color: INK.SECONDARY,
						}}
					>
						({currentDate.toFormat("ccc")})
					</Typography>
				</Box>
				{mode === "add-recurring" && (
					<InHouseTooltip title={"Recurring Shift"}>
						<RefreshOutlined fontSize={"inherit"} />
					</InHouseTooltip>
				)}
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<StaffAutoComplete
					width={minWidth}
					nurses={nurseListToShow}
					value={state.selectedNurse}
					onChange={handleNurseChange}
					disabled={isRosterLoading || isRosterError}
					helperText={isRosterError ? t("profile.roster.unavailable") : undefined}
					endAdornment={(option) => {
						const isCredentialed = credentialedNurseIds.includes(option.value.id);
						return isCredentialed ? null : (
							<InHouseTooltip title={"Not credentialed for selected facility"}>
								<WarningAmberOutlined
									fontSize={"inherit"}
									sx={{
										color: alpha(STATUS.ERROR.main, 0.8),
									}}
								/>
							</InHouseTooltip>
						);
					}}
				/>
				{state.selectedNurse && !credentialedNurseIds.includes(state.selectedNurse.value.id) && (
					<InHouseTooltip title={"Not credentialed for selected facility"}>
						<WarningAmberOutlined
							fontSize={"inherit"}
							sx={{
								color: alpha(STATUS.ERROR.main, 0.8),
							}}
						/>
					</InHouseTooltip>
				)}
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<FormControlLabel
					onChange={(_, checked) => toggleShowCredentialedOnly(checked)}
					control={<Checkbox checked={state.showCredentialedOnly} />}
					label={"Credentialed Staff Only"}
				/>
				{isLoadingCredentials && (
					<InHouseTooltip title="Loading credentials">
						<CircularProgress size={16} />
					</InHouseTooltip>
				)}
			</Box>
			<SystemAutoComplete
				options={facilitiesList}
				value={state.selectedFacility}
				onChange={onSelectedFacilityChange}
				textFieldProps={{
					inputSx: { minWidth: minWidth },
					placeholder: "Type Facility name/type or Section",
					label: "Facility",
					reserveHeightForHelperText: false,
				}}
				justifyContent={"start"}
				getOptionStartAdornment={(option) => <SynergyFacilityOptionStartAdornment facilityType={option.value.departmentType} />}
			/>
			{mode === "add-recurring" && (
				<FormControlLabel
					onChange={(_, checked) =>
						handleSetState({
							createForBothWeeks: checked,
						})
					}
					control={<Checkbox />}
					label={"Create recurring shift for both weeks"}
				/>
			)}
			<Box
				sx={{
					flex: "1",
					justifyContent: "end",
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<SystemButton buttonVariant={"secondary"} onClick={handleCloseClick} disabled={isCreateOrUpdateRecurringShiftsLoading}>
					Cancel
				</SystemButton>
				<SystemButton
					disabled={saveDisabled}
					tooltip={saveDisabled ? "Select staff member and facility to proceed" : undefined}
					buttonVariant={"primary"}
					onClick={handleSaveClick}
					loading={isCreateOrUpdateRecurringShiftsLoading}
				>
					Save
				</SystemButton>
			</Box>
		</Box>
	);
};
